import {
  Box,
  Card,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  IconButtonProps,
  Tooltip,
  Typography,
  styled
} from '@mui/material';
import {
  ResponseExtended,
  ResultDetailExtended
} from '../../../lib/readiness_gauge/readiness_gauge_instance';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KPIResponse from './KPIResponse';
import { KPI, Resource } from '@esg/esg-global-types';
import PrimaryDivider from '../../shared/decoration/PrimaryDivider';

interface KPIResultProps {
  kpi_detail: ResultDetailExtended;
  expanded: boolean;
  gri_resources: Array<Resource>;
  sdg_resources: Array<Resource>;
  handleChangeExpanded: (kpi: KPI, expanded: boolean) => void;
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled(
  (props: ExpandMoreProps): JSX.Element => {
    return <IconButton {...props} />;
  },
  {
    shouldForwardProp: (prop: string) => prop !== 'expand'
  }
)(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest
  })
}));

/**
 * Displays the response breakdown for a given KPI of the Readiness Gauge.
 * @param {ResultDetailExtended} kpi_detail Response details for KPI.
 * @param {boolean} expanded Expand or collapse component.
 * @param {Array<Resource>} gri_resources List of GRI related Resource Centre resources for responses to search through for relevant compliance reference link.
 * @param {Array<Resource>} sdg_resources List of SDG related Resource Centre resources for responses to search through for relevant sdg reference links.
 * @param {(kpi: KPI, expanded: boolean): void} handleChangeExpanded Controls whether the component is collapsed or expanded in parent.
 * @returns {JSX.Element}
 */
const KPIResult = ({
  kpi_detail,
  expanded,
  gri_resources,
  sdg_resources,
  handleChangeExpanded
}: KPIResultProps) => {
  const yes_responses: number = kpi_detail.responses.filter(
    (response: ResponseExtended) => response.response
  ).length;
  return (
    <Card sx={{ width: '100%', boxShadow: 'none', border: '1px solid #97A5AD' }}>
      <Grid container sx={{ alignItems: 'center', my: 1, mx: 2 }}>
        <Grid size={7}>
          <Typography
            color="primary"
            sx={{ fontSize: '1.2rem' }}
          >{`${kpi_detail.name}`}</Typography>
        </Grid>
        <Grid size={0.5}>
          <Box sx={{ height: '24px', display: 'flex' }}>
            <PrimaryDivider orientation="vertical" opacity={0.8} />
          </Box>
        </Grid>
        <Grid size={1}>
          <Typography
            color="primary"
            sx={{ fontSize: '1rem' }}
          >{`${Math.round((yes_responses / kpi_detail.responses.length) * 100)}%`}</Typography>
        </Grid>
        <Grid size={1}>
          <Typography
            color="primary"
            sx={{ fontSize: '1rem' }}
          >{`(${yes_responses}/${kpi_detail.responses.length})`}</Typography>
        </Grid>
        <Grid size={0.5}>
          <Tooltip title={<Typography fontSize="0.9rem">{kpi_detail.kpi.description}</Typography>}>
            <InfoOutlinedIcon color="primary" />
          </Tooltip>
        </Grid>
        <Grid size={2}>
          <ExpandMore
            expand={expanded}
            onClick={() => handleChangeExpanded(kpi_detail.kpi, !expanded)}
            aria-expanded={expanded}
            aria-label="show more"
            sx={{ color: '#7D8F99' }}
          >
            <ExpandMoreIcon />
          </ExpandMore>
        </Grid>
      </Grid>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          {kpi_detail.responses.map((response: ResponseExtended) => (
            <KPIResponse
              key={response.id}
              response={response}
              gri_resources={gri_resources}
              sdg_resources={sdg_resources}
            />
          ))}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default KPIResult;
